import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BmarkFixingType } from "./enums/BmarkFixingType.enum";

import { BmarkFixingDefinition } from "./BmarkFixingDefinition.model";
import { BmarkFillStatusMessageData } from "./BmarkFillStatusMessageData.model";
import { Type } from "@angular/core";
import { BmarkFixingTileColor } from "./enums/BmarkFixingTileColor.enum";

const FIXING_STATE_EXECUTION_ORDER = [
  CubiqTS.FixingState.ACTIVE,
  CubiqTS.FixingState.SOFT_CUTOFF,
  CubiqTS.FixingState.HARD_CUTOFF,
  CubiqTS.FixingState.FIXING_HIT,
  CubiqTS.FixingState.TRIGGERED,
  CubiqTS.FixingState.COMPLETED,
];

export class BmarkFixingData {
  public id: string; // fixingSourceCd + fixingDateString
  public code: string;
  public fixingTimestamp: number; // unix timestamp in milliseconds (offset from 1/1/1970)
  private clientOrderIds: Set<string> = new Set();
  private hedgeOrderIds: Set<string> = new Set();
  private _state: CubiqTS.FixingState;
  private _type: BmarkFixingType;

  // display
  public displayCode: string;
  public displayCodeTooltip: string;
  public displayDate: string;
  public displayTime: string;
  public displayColor: BmarkFixingTileColor;
  public forceReload: boolean = false;
  public source: Type<any> = null;
  public lastRevalTime: CubiqTS.Moment = null;

  // purge timestamp
  public purgeTimestamp: number;
  // fixing definition, data derived form BenchmarkFixingImpl
  public fixingDef: BmarkFixingDefinition = new BmarkFixingDefinition();

  private fillStatusMessage: BmarkFillStatusMessageData = new BmarkFillStatusMessageData();

  private allocationCount: number = 0;
  private deallocated: boolean = false;

  private static allocatorMap: Map<string, BmarkFixingData> = new Map();

  public incorrectShouldBeSwept = false;

  /** Public ID that matches the fixing  */
  public fixingId: CubiqTS.BMTimeCodeImpl;

  public get state(): CubiqTS.FixingState {
    return this._state;
  }

  public set state(state: CubiqTS.FixingState) {
    if (
      FIXING_STATE_EXECUTION_ORDER.indexOf(state) <
      FIXING_STATE_EXECUTION_ORDER.indexOf(this._state)
    ) {
      logger.out.warn(
        `${this.constructor.name} prevented state rollback: ${this.id} ${
          CubiqTS.FixingState[this._state]
        } -> ${CubiqTS.FixingState[state]} `,
      );
      return;
    }

    this._state = state;
  }

  public get type(): BmarkFixingType {
    return this._type;
  }

  public set type(type: BmarkFixingType) {
    let updateId = type !== this._type;

    this._type = type;

    if (this.fillStatusMessage == null) this.fillStatusMessage = new BmarkFillStatusMessageData();

    if (updateId)
      this.id =
        this.code +
        ":" +
        this.displayDate +
        ":" +
        this.displayTime +
        ":" +
        BmarkFixingType[this._type];
  }

  public get hasOrders(): boolean {
    return this.clientOrderIds.size > 0 || this.hedgeOrderIds.size > 0;
  }
  public get clientOrdersCount(): number {
    return this.clientOrderIds.size;
  }

  public get hedgeOrdersCount(): number {
    return this.hedgeOrderIds.size;
  }

  // this must sync with BmarkFixingData.create()
  public static id(
    code: string,
    fixingDateTimeMoment: CubiqTS.Moment,
    type: BmarkFixingType,
  ): string {
    if (typeof code === "string" && fixingDateTimeMoment != null) {
      let displayDate = fixingDateTimeMoment.format("DD-MMM-YY");
      let displayTime = fixingDateTimeMoment.format("HH:mm");
      let id = code + ":" + displayDate + ":" + displayTime + ":" + BmarkFixingType[type];

      return id;
    } else {
      throw Error(`Benchmark fixing generation failed: invalid code or date-time`);
    }
  }

  // this must sync with BmarkFixingData.id()
  public static create(
    code: string,
    fixingDateTimeMoment: CubiqTS.Moment,
    type: BmarkFixingType,
    fixingDef: BmarkFixingDefinition,
  ): BmarkFixingData {
    let bmark: BmarkFixingData = null;

    if (typeof code === "string" && fixingDateTimeMoment != null) {
      let displayDate = fixingDateTimeMoment.format("DD-MMM-YY");
      let displayTime = fixingDateTimeMoment.format("HH:mm");
      let id = code + ":" + displayDate + ":" + displayTime + ":" + BmarkFixingType[type];

      if (BmarkFixingData.allocatorMap.has(id)) {
        bmark = BmarkFixingData.allocatorMap.get(id);
        if (bmark.deallocated) {
          logger.out.warn(
            `${this.constructor.name}.create() reseting deallocated reference. Suspect tampering`,
          );
          bmark.deallocated = false;
          bmark.allocationCount = 0;
        }
      } else {
        bmark = new BmarkFixingData();
        // TODO Please consider GC implications of this careful.
        BmarkFixingData.allocatorMap.set(id, bmark);
      }
      // update all properties in case of updates
      bmark.displayDate = displayDate;
      bmark.displayTime = displayTime;
      bmark.displayCode = code.length > 6 ? code.substring(0, 6) + ".." : code;
      bmark.displayCodeTooltip = code + ":" + displayDate + ":" + displayTime;

      bmark.id = id;
      bmark.code = code;
      bmark.type = type;
      bmark.fixingTimestamp = parseInt(fixingDateTimeMoment.format("x"));
      const fixingId = new CubiqTS.BMTimeCodeImpl();
      fixingId.code = code;
      fixingId.time = CubiqTS.moment(fixingDateTimeMoment);
      bmark.fixingId = fixingId;
      bmark.fixingDef = fixingDef;
      BmarkFixingData.retain(bmark);
      return bmark;
    } else {
      throw Error(`Benchmark fixing generation failed: invalid code or date-time`);
    }
  }

  public static destroy(bmarkFixing: BmarkFixingData): null {
    if (bmarkFixing instanceof BmarkFixingData) {
      if (BmarkFixingData.allocatorMap.has(bmarkFixing.id)) {
        let allocatedBmark = BmarkFixingData.allocatorMap.get(bmarkFixing.id);

        if (bmarkFixing !== allocatedBmark) {
          logger.out.warn(
            `BmarkFixingData Allocator Bypassed. will not destroy() allocated object`,
          );
          logger.out.warn("BmarkFixingData.destroy() requested: %O", bmarkFixing);
          logger.out.warn("BmarkFixingData.destroy() allocated: %O", allocatedBmark);
          //
          bmarkFixing.deallocated = true;
        }

        if (bmarkFixing.allocationCount > 0) BmarkFixingData.release(bmarkFixing);
        else
          logger.out.warn(
            `BmarkFixingData Allocator allocationCount:${bmarkFixing.allocationCount} <= 0`,
          );

        if (bmarkFixing.allocationCount === 0) {
          bmarkFixing.deallocated = true;
          // hopefully no one is retaining a copy at this point
          logger.out.info(
            `BmarkFixingData destroy() success for allocated object id:${bmarkFixing.id}`,
          );
          BmarkFixingData.allocatorMap.delete(bmarkFixing.id);
        } else if (bmarkFixing.allocationCount === 1) {
          // destroy() calls from both BenchmarkDataLookupService/HedgeOrderDataLookupService
          logger.out.info(
            `BmarkFixingData not ready to destroy() allocated object id:${bmarkFixing.id}` +
              `allocationCount:${bmarkFixing.allocationCount}`,
          );
        } else {
          // a release() call has been missed
          logger.out.warn(
            `BmarkFixingData can not destroy() allocated object id:${bmarkFixing.id}` +
              `allocationCount:${bmarkFixing.allocationCount}`,
          );
        }
      }
    }

    return null;
  }

  public static retain(bmarkFixing: BmarkFixingData): BmarkFixingData {
    if (bmarkFixing.allocationCount < 0 || bmarkFixing.deallocated)
      logger.out.error(
        `BmarkFixingData Allocator allocationCount:${bmarkFixing.allocationCount} <= 0 error`,
      );

    bmarkFixing.allocationCount++;
    return bmarkFixing;
  }

  public static release(bmarkFixing: BmarkFixingData): BmarkFixingData {
    if (bmarkFixing.allocationCount <= 0)
      logger.out.warn(
        `BmarkFixingData Allocator allocationCount:${bmarkFixing.allocationCount} <= 0 error`,
      );

    bmarkFixing.allocationCount--;

    return bmarkFixing;
  }

  public static generateFixingDataLogMsg(orderData: CubiqTS.OrderDataImpl): string | null {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      typeof orderData.fixingSourceCd === "string" &&
      orderData.fixingDate != null
    ) {
      try {
        let date: string = orderData.fixingDate.format("DD-MMM-YY");
        let time: string = orderData.fixingDate.format("HH:mm");
        // sort of like a id, except we don't know type
        return orderData.fixingSourceCd + ":" + date + ":" + time;
      } catch (ex) {
        logger.out.error(`ERROR: generateFixingIdFromOrderData(): ${ex}`);
        return null;
      }
    } else {
      return null;
    }
  }

  public static generateFixingIdFromOrderData(
    orderData: CubiqTS.OrderDataImpl,
    fixingType: BmarkFixingType,
  ): string | null {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      typeof orderData.fixingSourceCd === "string" &&
      orderData.fixingDate != null
    ) {
      try {
        let date: string = orderData.fixingDate.format("DD-MMM-YY");
        let time: string = orderData.fixingDate.format("HH:mm");
        return (
          orderData.fixingSourceCd + ":" + date + ":" + time + ":" + BmarkFixingType[fixingType]
        );
      } catch (ex) {
        logger.out.error(`ERROR: generateFixingIdFromOrderData(): ${ex}`);
        return null;
      }
    } else {
      return null;
    }
  }

  public static generateFixingIdFromCodeMoment(
    code: string,
    moment: CubiqTS.Moment,
    fixingType: BmarkFixingType,
  ): string | null {
    if (typeof code === "string" && moment != null) {
      // instanceof CubiqTS.moment.Moment
      try {
        let date: string = moment.format("DD-MMM-YY");
        let time: string = moment.format("HH:mm");
        let suffix: string = fixingType in BmarkFixingType ? ":" + BmarkFixingType[fixingType] : "";
        return code + ":" + date + ":" + time + suffix;
      } catch (ex) {
        logger.out.error(`ERROR: generateFixingIdFromCodeMoment(): ${ex}`);
        return null;
      }
    } else {
      return null;
    }
  }

  public static isActiveId(fixingId: string) {
    return (
      typeof fixingId == "string" &&
      fixingId.endsWith(`:${BmarkFixingType[BmarkFixingType.ACTIVE]}`)
    );
  }

  public static isRecentId(fixingId: string) {
    return (
      typeof fixingId == "string" &&
      fixingId.endsWith(`:${BmarkFixingType[BmarkFixingType.RECENT]}`)
    );
  }

  public static getIdType(fixingId: string): BmarkFixingType | null {
    if (BmarkFixingData.isActiveId(fixingId)) return BmarkFixingType.ACTIVE;

    if (BmarkFixingData.isRecentId(fixingId)) return BmarkFixingType.RECENT;

    return null;
  }

  public static getIdAsActive(fixingId: string): string {
    if (BmarkFixingData.isActiveId(fixingId)) return fixingId;

    if (BmarkFixingData.isRecentId(fixingId))
      return fixingId.replace(/:(\w+)$/, `:${BmarkFixingType[BmarkFixingType.ACTIVE]}`);

    return null;
  }

  public static getIdAsRecent(fixingId: string): string {
    if (BmarkFixingData.isActiveId(fixingId))
      return fixingId.replace(/:(\w+)$/, `:${BmarkFixingType[BmarkFixingType.RECENT]}`);

    if (BmarkFixingData.isRecentId(fixingId)) return fixingId;

    return null;
  }

  public updateFillStatusMessage(
    orderData: CubiqTS.OrderData,
    traderGroup: CubiqTS.TradingGroupImpl,
  ) {
    this.fillStatusMessage.update(orderData, traderGroup);
  }

  public removeFromFillStatusMessage(orderData: CubiqTS.OrderData | string) {
    this.fillStatusMessage.remove(orderData);
  }

  public i18nFillStatusMessage(): string {
    return this.fillStatusMessage.i18nFillStatusMessage(this.type, this.state);
  }

  public updateDisplayColor(): BmarkFixingTileColor {
    try {
      this.displayColor = this.fillStatusMessage.getDisplayColor(
        this.type,
        this.state,
        this.fixingTimestamp,
      );
      //logger.out.warn(`WARN  ${this.constructor.name}.determineDisplayColor(): INVOKED [${this.displayTime + ' ' + this.displayCode + ' (' + this.displayDate+ ')'}] => ${BmarkFixingTileColor[this.displayColor]} `);
    } catch (ex) {
      this.displayColor = BmarkFixingTileColor.TODAY;
    }
    return this.displayColor;
  }

  public clientOrdersSize(): number {
    return this.clientOrderIds.size;
  }

  public hasClientOrders(): boolean {
    return this.clientOrderIds.size > 0;
  }

  public getClientOrderIds(): string[] {
    return Array.from(this.clientOrderIds);
  }

  public clearClientOrders(): void {
    this.clientOrderIds.clear();
  }

  public hasHedgeOrders(): boolean {
    return this.hedgeOrderIds.size > 0;
  }

  public getHedgeOrderIds(): string[] {
    return Array.from(this.hedgeOrderIds);
  }

  public clearHedgeOrders(): void {
    this.hedgeOrderIds.clear();
  }

  public addClientOrder(orderData: CubiqTS.OrderDataImpl, traderGroup: CubiqTS.TradingGroupImpl) {
    this.clientOrderIds.add(orderData.orderId.toString());
    this.updateFillStatusMessage(orderData, traderGroup);
  }

  public updateClientOrder(
    orderData: CubiqTS.OrderDataImpl,
    traderGroup: CubiqTS.TradingGroupImpl,
  ) {
    if (!this.clientOrderIds.has(orderData.orderId.toString())) {
      logger.out.error(
        `${this.constructor.name}.updateClientOrder() missing orderId from fixing id: ${
          this.id
        } ${orderData.orderId.toString()} %O`,
        orderData,
      );
      return;
    }

    this.updateFillStatusMessage(orderData, traderGroup);
  }

  public setClientOrders(
    clientOrders: Array<{
      orderData: CubiqTS.OrderDataImpl;
      traderGroup: CubiqTS.TradingGroupImpl;
    }>,
  ) {
    let oldOrderIds: string[] =
      this.clientOrderIds instanceof Set ? Array.from(this.clientOrderIds.values()) : [];

    if (Array.isArray(oldOrderIds) && oldOrderIds.length > 0) {
      oldOrderIds = oldOrderIds.filter((a: string) => {
        if (this.clientOrderIds.has(a)) return false;

        this.removeFromFillStatusMessage(a);

        return true;
      });
    }

    this.clientOrderIds = new Set<string>(
      clientOrders.map((clientOrder) => {
        this.updateFillStatusMessage(clientOrder.orderData, clientOrder.traderGroup);
        return clientOrder.orderData.orderId.toString();
      }),
    );

    return oldOrderIds;
  }

  public hasClientOrderId(orderData: CubiqTS.OrderDataImpl | string): boolean {
    let orderId: string =
      orderData instanceof CubiqTS.OrderDataImpl ? orderData.orderId.toString() : orderData;
    return this.clientOrderIds.has(orderId);
  }

  public removeClientOrder(orderData: CubiqTS.OrderDataImpl | string) {
    let orderId: string =
      orderData instanceof CubiqTS.OrderDataImpl ? orderData.orderId.toString() : orderData;
    this.clientOrderIds.delete(orderId);
    this.removeFromFillStatusMessage(orderId);
  }

  public addHedgeOrder(orderData, traderGroup: CubiqTS.TradingGroupImpl) {
    this.hedgeOrderIds.add(orderData.orderId.toString());
    this.updateFillStatusMessage(orderData, traderGroup);
  }

  public updateHedgeOrder(orderData: CubiqTS.OrderDataImpl, traderGroup: CubiqTS.TradingGroupImpl) {
    if (!this.hedgeOrderIds.has(orderData.orderId.toString())) {
      logger.out.error(
        `${this.constructor.name}.updateHedgeOrder() missing orderId from fixing id: ${
          this.id
        } ${orderData.orderId.toString()} %O`,
        orderData,
      );
      return;
    }

    this.updateFillStatusMessage(orderData, traderGroup);
  }

  public setHedgeOrders(
    hedgeOrders: Array<{
      orderData: CubiqTS.OrderDataImpl;
      traderGroup: CubiqTS.TradingGroupImpl;
    }>,
  ) {
    let oldOrderIds: string[] =
      this.hedgeOrderIds instanceof Set ? Array.from(this.hedgeOrderIds.values()) : [];

    if (Array.isArray(oldOrderIds) && oldOrderIds.length > 0) {
      oldOrderIds = oldOrderIds.filter((a: string) => {
        if (this.hedgeOrderIds.has(a)) return false;

        this.removeFromFillStatusMessage(a);

        return true;
      });
    }

    this.hedgeOrderIds = new Set<string>(
      hedgeOrders.map((hedgeOrder) => {
        this.updateFillStatusMessage(hedgeOrder.orderData, hedgeOrder.traderGroup);
        return hedgeOrder.orderData.orderId.toString();
      }),
    );

    return oldOrderIds;
  }

  public hasHedgeOrderId(orderData: CubiqTS.OrderDataImpl | string): boolean {
    let orderId: string =
      orderData instanceof CubiqTS.OrderDataImpl ? orderData.orderId.toString() : orderData;
    return this.hedgeOrderIds.has(orderId);
  }

  public removeHedgeOrder(orderData: CubiqTS.OrderDataImpl | string) {
    let orderId: string =
      orderData instanceof CubiqTS.OrderDataImpl ? orderData.orderId.toString() : orderData;
    this.hedgeOrderIds.delete(orderId);
    this.removeFromFillStatusMessage(orderId);
  }

  public toString(): string {
    return this.constructor.name + " [" + this.code + " @ " + this.fixingTimestamp.toString() + "]";
  }

  /**
   * Method to clear allocator map.
   * NOTE: Must not be invoked at runtime this is a helper method for unit tests.
   */
  public static clearAllocatorMap(): void {
    if (window["jasmine"] != null) {
      if (BmarkFixingData.allocatorMap) {
        BmarkFixingData.allocatorMap.clear();
      }
    } else {
      throw "BmarkFixingData::clearAllocatorMap Could not detect test environment";
    }
  }
}
